import { Slot } from "@radix-ui/react-slot";
import { ChevronRight, MoreHorizontal } from "lucide-react";
import { ComponentProps, FC } from "react";

import { cn } from "@/lib/utils";
import { PropsAsChild } from "@/shared/types";

type BreadcrumbProps = ComponentProps<"nav">;
type BreadcrumbListProps = ComponentProps<"ol">;
type BreadcrumbItemProps = ComponentProps<"li">;
type BreadcrumbLinkProps = PropsAsChild<ComponentProps<"a">>;
type BreadcrumbPageProps = ComponentProps<"span">;
type BreadcrumbSeparatorProps = ComponentProps<"li">;
type BreadcrumbEllipsisProps = ComponentProps<"span">;

export const Breadcrumb: FC<BreadcrumbProps> = (props) => (
  <nav aria-label="breadcrumb" data-slot="breadcrumb" {...props} />
);

export const BreadcrumbList: FC<BreadcrumbListProps> = ({
  className,
  ...props
}) => (
  <ol
    data-slot="breadcrumb-list"
    className={cn(
      "text-muted-foreground flex flex-wrap items-center gap-1.5 text-sm break-words sm:gap-2.5",
      className,
    )}
    {...props}
  />
);

export const BreadcrumbItem: FC<BreadcrumbItemProps> = ({
  className,
  ...props
}) => (
  <li
    data-slot="breadcrumb-item"
    className={cn("inline-flex items-center gap-1.5", className)}
    {...props}
  />
);

export const BreadcrumbLink: FC<BreadcrumbLinkProps> = ({
  asChild,
  className,
  ...props
}) => {
  const Comp = asChild ? Slot : "a";

  return (
    <Comp
      data-slot="breadcrumb-link"
      className={cn("hover:text-foreground transition-colors", className)}
      {...props}
    />
  );
};

export const BreadcrumbPage: FC<BreadcrumbPageProps> = ({
  className,
  ...props
}) => (
  <span
    data-slot="breadcrumb-page"
    role="link"
    aria-disabled="true"
    aria-current="page"
    className={cn("text-foreground font-normal", className)}
    {...props}
  />
);

export const BreadcrumbSeparator: FC<BreadcrumbSeparatorProps> = ({
  children,
  className,
  ...props
}) => (
  <li
    data-slot="breadcrumb-separator"
    role="presentation"
    aria-hidden="true"
    className={cn("[&>svg]:size-3.5", className)}
    {...props}
  >
    {children ?? <ChevronRight />}
  </li>
);

export const BreadcrumbEllipsis: FC<BreadcrumbEllipsisProps> = ({
  className,
  ...props
}) => (
  <span
    data-slot="breadcrumb-ellipsis"
    role="presentation"
    aria-hidden="true"
    className={cn("flex size-9 items-center justify-center", className)}
    {...props}
  >
    <MoreHorizontal className="size-4" />
    <span className="sr-only">More</span>
  </span>
);
